/**
 * <pdp-carousel-video>
 *
 * Muted, looped, inline autoplay for a self-hosted PDP carousel video slide.
 * The video plays only while its slide is within the viewport (i.e. the active
 * carousel slide) and pauses otherwise. Because the <video> is preload="none",
 * its bytes are not fetched until the user swipes the slide into view — so the
 * feature costs nothing on initial page load (mobile-first performance).
 *
 * Progressive enhancement: if this script never runs the <video> simply shows
 * its poster frame. Respects prefers-reduced-motion (poster stays, no playback).
 */
if (!customElements.get('pdp-carousel-video')) {
  customElements.define(
    'pdp-carousel-video',
    class PdpCarouselVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video || this.observer) return;

        // Reduced motion: leave the poster still in place, never autoplay.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        // Property form is required for autoplay eligibility on iOS Safari.
        this.video.muted = true;

        if (!('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                this.play();
              } else {
                this.video.pause();
              }
            });
          },
          // Viewport-relative: a slide scrolled horizontally off-screen reports
          // as not-intersecting, so only the on-screen slide plays.
          { threshold: [0, 0.5, 1] }
        );
        this.observer.observe(this);
      }

      play() {
        const attempt = this.video.play();
        // Ignore autoplay-policy rejections (e.g. Low Power Mode).
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }
    }
  );
}
